// src/pages/statistics/TourStatisticsPage.tsx
import React, { useEffect, useMemo, useState } from "react";
import PageHeader from "../../components/ui/PageHeader";
import Card from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import Field from "../../components/ui/Field";
import Input from "../../components/ui/Input";
import { Download } from "lucide-react";
import {
    getStatisticsFiltersRequest,
    getTourStatisticsRequest,
    exportTourStatisticsRequest,
    triggerBrowserDownload,
    StatsFilterTour,
    StatsRow,
    ToursStatisticsResponse,
} from "../../services/api/statistics.api";
import { formatMoney, formatDate, pluralizePaymentTypes } from "./statsFormat";

const TourStatisticsPage: React.FC = () => {
    const [tours, setTours] = useState<StatsFilterTour[]>([]);
    const [filtersLoading, setFiltersLoading] = useState(true);
    const [filtersError, setFiltersError] = useState<string | null>(null);

    const [selectedTourId, setSelectedTourId] = useState("");
    const [selectedTourCityId, setSelectedTourCityId] = useState("");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");

    const [data, setData] = useState<ToursStatisticsResponse | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [pageError, setPageError] = useState<string | null>(null);

    const [isExporting, setIsExporting] = useState(false);
    const [exportMessage, setExportMessage] = useState<string | null>(null);

    useEffect(() => {
        (async () => {
            try {
                const res = await getStatisticsFiltersRequest();
                setTours(res.tours);
            } catch (e) {
                setFiltersError(e instanceof Error ? e.message : "Не удалось загрузить фильтры");
            } finally {
                setFiltersLoading(false);
            }
        })();
    }, []);

    const selectedTour = useMemo(
        () => tours.find((t) => t.id === selectedTourId) || null,
        [tours, selectedTourId]
    );

    const loadStatistics = async () => {
        setIsLoading(true);
        setPageError(null);
        try {
            const res = await getTourStatisticsRequest({
                tourId: selectedTourId || undefined,
                tourCityId: selectedTourCityId || undefined,
                dateFrom: dateFrom || undefined,
                dateTo: dateTo || undefined,
            });
            setData(res);
        } catch (e) {
            setPageError(e instanceof Error ? e.message : "Не удалось загрузить статистику");
        } finally {
            setIsLoading(false);
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        loadStatistics();
    }, [selectedTourId, selectedTourCityId, dateFrom, dateTo]);

    const handleTourChange = (value: string) => {
        setSelectedTourId(value);
        setSelectedTourCityId("");
    };

    const handleExport = async () => {
        setIsExporting(true);
        setExportMessage(null);
        try {
            const result = await exportTourStatisticsRequest({
                tourId: selectedTourId || undefined,
                tourCityId: selectedTourCityId || undefined,
                dateFrom: dateFrom || undefined,
                dateTo: dateTo || undefined,
            });
            if (result.background) {
                setExportMessage(result.message || "Отчёт формируется в фоне и будет отправлен на почту.");
            } else if (result.blob && result.filename) {
                triggerBrowserDownload(result.blob, result.filename);
            }
        } catch (e) {
            setExportMessage(e instanceof Error ? e.message : "Не удалось выполнить выгрузку");
        } finally {
            setIsExporting(false);
        }
    };

    const renderStatsCells = (row: StatsRow) => (
        <>
            <td className="stats-cell-numeric">{row.seats_total}</td>
            <td className="stats-cell-numeric">{row.seats_free}</td>
            <td className="stats-cell-numeric">{row.seats_admin_reserved}</td>
            <td className="stats-cell-numeric">
                {row.orders_count}{" "}
                <span className="stats-cell-muted">({row.tickets_sold} бил.)</span>
            </td>
            <td className="stats-cell-numeric">{formatMoney(row.orders_sum)}</td>
            <td className="stats-cell-numeric">
                {row.unpaid_orders_count}{" "}
                <span className="stats-cell-muted">
                    ({row.unpaid_tickets_count} бил., {formatMoney(row.unpaid_sum)})
                </span>
            </td>
            <td className="stats-cell-numeric">
                {row.paid_orders_count}{" "}
                <span className="stats-cell-muted">
                    ({row.paid_tickets_count} бил., {formatMoney(row.paid_sum)})
                </span>
            </td>
            <td className="stats-cell-numeric">{row.regular_orders_count}</td>
            <td className="stats-cell-numeric">{row.agent_orders_count}</td>
            <td className="stats-cell-numeric">{row.invite_tickets_count}</td>
            <td>
                {row.payment_breakdown.length === 0 ? (
                    <span className="stats-cell-muted">—</span>
                ) : (
                    <details className="stats-payment-details">
                        <summary className="stats-payment-summary">
                            {pluralizePaymentTypes(row.payment_breakdown.length)}
                        </summary>
                        <div className="stats-payment-breakdown">
                            {row.payment_breakdown.map((pb) => (
                                <div className="stats-payment-line" key={pb.payment_type}>
                                    <span className="stats-payment-type">{pb.payment_type}</span>
                                    <span>
                                        Опл.: {pb.paid_orders_count} зак. / {pb.paid_count} бил.{" "}
                                        <span className="stats-cell-muted">({formatMoney(pb.paid_sum)})</span>
                                    </span>
                                    <span>
                                        Не опл.: {pb.unpaid_orders_count} зак. / {pb.unpaid_count} бил.{" "}
                                        <span className="stats-cell-muted">({formatMoney(pb.unpaid_sum)})</span>
                                    </span>
                                </div>
                            ))}
                        </div>
                    </details>
                )}
            </td>
        </>
    );

    return (
        <div className="page-content">
            <PageHeader
                title="Статистика по турам и городам"
                description="Продажи, оплаты и загрузка залов по каждому туру и его городам."
                actions={
                    <Button variant="secondary" onClick={handleExport} disabled={isExporting}>
                        <Download size={16} style={{ marginRight: 6 }} />
                        {isExporting ? "Формируем..." : "Выгрузить в Excel"}
                    </Button>
                }
            />

            <Card>
                <section className="section-toolbar">
                    <div>
                        <h3 className="card-title">Фильтры</h3>
                        <p className="card-text">
                            Выберите тур, город и период — таблица и выгрузка обновятся автоматически.
                        </p>
                    </div>
                </section>

                <form className="stats-filter-bar" onSubmit={(e) => e.preventDefault()}>
                    <div className="stats-filter-row">
                        <Field label="Тур" htmlFor="stats-tour">
                            <select
                                id="stats-tour"
                                className="ui-select"
                                value={selectedTourId}
                                onChange={(e) => handleTourChange(e.target.value)}
                                disabled={filtersLoading}
                            >
                                <option value="">
                                    {filtersLoading
                                        ? "Загрузка..."
                                        : filtersError
                                            ? "Ошибка загрузки"
                                            : "Все туры"}
                                </option>
                                {tours.map((tour) => (
                                    <option key={tour.id} value={tour.id}>
                                        {tour.name}
                                    </option>
                                ))}
                            </select>
                        </Field>

                        {selectedTour && (
                            <Field label="Город тура" htmlFor="stats-tour-city">
                                <select
                                    id="stats-tour-city"
                                    className="ui-select"
                                    value={selectedTourCityId}
                                    onChange={(e) => setSelectedTourCityId(e.target.value)}
                                >
                                    <option value="">Все города</option>
                                    {selectedTour.cities.map((city) => (
                                        <option key={city.id} value={city.id}>
                                            {formatDate(city.start_at) ? `${city.name} — ${formatDate(city.start_at)}` : city.name}
                                        </option>
                                    ))}
                                </select>
                            </Field>
                        )}

                        <Field label="Дата от" htmlFor="stats-date-from">
                            <Input
                                id="stats-date-from"
                                type="date"
                                value={dateFrom}
                                onChange={(e) => setDateFrom(e.target.value)}
                            />
                        </Field>

                        <Field label="Дата до" htmlFor="stats-date-to">
                            <Input
                                id="stats-date-to"
                                type="date"
                                value={dateTo}
                                onChange={(e) => setDateTo(e.target.value)}
                            />
                        </Field>
                    </div>
                </form>

                {exportMessage && <div className="stats-export-message">{exportMessage}</div>}
                {pageError && <div className="form-global-error">{pageError}</div>}

                {isLoading ? (
                    <div className="table-skeleton">
                        <div className="table-skeleton-row" />
                        <div className="table-skeleton-row" />
                        <div className="table-skeleton-row" />
                    </div>
                ) : !data || data.tours.length === 0 ? (
                    <div className="empty-block">
                        <h3 className="empty-block-title">Данных нет</h3>
                        <p className="empty-block-text">Попробуйте изменить фильтры или период.</p>
                    </div>
                ) : (
                    <div className="table-wrap">
                        <table className="data-table stats-table">
                            <thead>
                            <tr>
                                <th>Тур / город</th>
                                <th>Мест всего</th>
                                <th>Свободно</th>
                                <th>Админ-бронь</th>
                                <th>Заказов</th>
                                <th>Сумма</th>
                                <th>Не оплачено</th>
                                <th>Оплачено</th>
                                <th>Обычные</th>
                                <th>Распространители</th>
                                <th>Пригласительные</th>
                                <th>Оплаты по типам</th>
                            </tr>
                            </thead>
                            <tbody>
                            {data.tours.map((tour) => (
                                <React.Fragment key={tour.id}>
                                    <tr className="stats-tour-row">
                                        <td className="stats-row-name">{tour.name}</td>
                                        {renderStatsCells(tour)}
                                    </tr>
                                    {tour.cities.map((city) => (
                                        <tr className="stats-city-row" key={city.id}>
                                            <td className="stats-row-name">
                                                {city.name}
                                                {city.start_at && (
                                                    <span className="stats-cell-muted"> — {formatDate(city.start_at)}</span>
                                                )}
                                            </td>
                                            {renderStatsCells(city)}
                                        </tr>
                                    ))}
                                </React.Fragment>
                            ))}
                            <tr className="stats-total-row">
                                <td className="stats-row-name">{data.total.name}</td>
                                {renderStatsCells(data.total)}
                            </tr>
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>
        </div>
    );
};

export default TourStatisticsPage;
